import { useQuery } from "@tanstack/react-query"
import { useThemeContext } from "@tokenterminal/ui/Theme/ThemeContext"

export const getContrastingColor = (color: string) => {
  const rgbColor = hexToRgb(color)
  return calculateContrast(rgbColor, [255, 255, 255]) > 8.5
    ? "#FFFFFF"
    : "#000000"
}

// Constants for luminance calculation
const RED = 0.2126
const GREEN = 0.7152
const BLUE = 0.0722
const GAMMA = 2.4

// Convert hex color to RGB array
const hexToRgb = (hex: string): number[] => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return [r, g, b]
}

// Calculate luminance for a color
const calculateLuminance = (r: number, g: number, b: number): number => {
  const a = [r, g, b].map((v) => {
    v /= 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, GAMMA)
  })
  return a[0] * RED + a[1] * GREEN + a[2] * BLUE
}

// Calculate contrast ratio between two RGB colors
const calculateContrast = (rgb1: number[], rgb2: number[]): number => {
  const lum1 = calculateLuminance(rgb1[0], rgb1[1], rgb1[2])
  const lum2 = calculateLuminance(rgb2[0], rgb2[1], rgb2[2])
  const brightest = Math.max(lum1, lum2)
  const darkest = Math.min(lum1, lum2)
  return (brightest + 0.05) / (darkest + 0.05)
}

// Calculate color distance using Euclidean distance in RGB space
const calculateColorDistance = (hex1: string, hex2: string): number => {
  const rgb1 = hexToRgb(hex1)
  const rgb2 = hexToRgb(hex2)

  return Math.sqrt(
    Math.pow(rgb1[0] - rgb2[0], 2) +
      Math.pow(rgb1[1] - rgb2[1], 2) +
      Math.pow(rgb1[2] - rgb2[2], 2)
  )
}

// Check if a color has enough contrast with white or black based on theme
const hasEnoughContrast = (
  hex: string,
  theme: "light" | "dark",
  contrastRatioThreshold: number = 4.5
) => {
  const rgbColor = hexToRgb(hex)
  // Check against white for light theme, black for dark theme
  const contrastColor = theme === "light" ? [255, 255, 255] : [0, 0, 0]
  const contrastRatio = calculateContrast(rgbColor, contrastColor)

  // WCAG recommends a contrast ratio of at least 4.5:1
  return contrastRatio >= contrastRatioThreshold
}

export const useKeyColors = (imgUrl?: string | null) => {
  const { theme } = useThemeContext()
  return useQuery({
    queryKey: ["accentColor", imgUrl, theme],
    queryFn: async () => {
      const img = new Image()
      img.crossOrigin = "Anonymous"
      img.src = imgUrl ?? ""

      return new Promise<{
        color: string
        contrastingColor: string
        uniqueColors: string[]
      }>((resolve) => {
        img.onload = () => {
          const canvas = document.createElement("canvas")
          const ctx = canvas.getContext("2d")
          if (!ctx)
            return resolve({
              color: "#000000",
              contrastingColor: "#000000",
              uniqueColors: ["#000000"],
            })

          canvas.width = img.width
          canvas.height = img.height
          ctx.drawImage(img, 0, 0)

          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const pixels = imageData.data

          // Count frequency of colors
          const colorMap: Record<string, number> = {}
          // Track color saturation and brightness
          const colorStats: Record<
            string,
            { saturation: number; brightness: number }
          > = {}

          for (let i = 0; i < pixels.length; i += 4) {
            const r = pixels[i]
            const g = pixels[i + 1]
            const b = pixels[i + 2]
            const a = pixels[i + 3]

            // Skip transparent pixels
            if (a < 128) continue

            const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`

            // Calculate saturation and brightness
            const max = Math.max(r, g, b)
            const min = Math.min(r, g, b)
            const delta = max - min
            const brightness = max / 255
            const saturation = max === 0 ? 0 : delta / max

            colorMap[hex] = (colorMap[hex] || 0) + 1
            colorStats[hex] = { saturation, brightness }
          }

          // Sort colors by frequency
          const sortedColors = Object.entries(colorMap).sort(
            ([, count1], [, count2]) => count2 - count1
          )

          // Find most common color with enough contrast
          let dominantHex: string | undefined

          // Check the first 10 colors for sufficient contrast
          for (let i = 0; i < Math.min(10, sortedColors.length); i++) {
            const [hex, _] = sortedColors[i]
            if (hasEnoughContrast(hex, theme ?? "light", 1.5)) {
              dominantHex = hex
              break
            }
          }

          // If no color with enough contrast was found, use default
          if (!dominantHex) {
            dominantHex = theme === "light" ? "#000000" : "#FFFFFF"
          }

          const contrastingHex =
            calculateContrast(hexToRgb(dominantHex), [255, 255, 255]) > 6.5
              ? "#FFFFFF"
              : "#000000"

          // Get unique colors that are sufficiently different
          const uniqueColors: string[] = []
          const COLOR_DISTANCE_THRESHOLD = 65 // Adjust this threshold as needed
          const MAX_UNIQUE_COLORS = 5

          // Start with the most frequent colors
          for (const [hex, count] of sortedColors) {
            // Skip colors that are too similar to already selected colors
            const isTooSimilar = uniqueColors.some(
              (existingColor) =>
                calculateColorDistance(hex, existingColor) <
                COLOR_DISTANCE_THRESHOLD
            )

            if (!isTooSimilar) {
              uniqueColors.push(hex)
            }

            // Stop once we have enough unique colors
            if (uniqueColors.length >= MAX_UNIQUE_COLORS) {
              break
            }
          }

          resolve({
            color: dominantHex,
            contrastingColor: contrastingHex,
            uniqueColors,
          })
        }
      })
    },
    // 1 hour
    // staleTime: 1000 * 60 * 60,
    staleTime: 0,
    enabled: !!imgUrl,
  })
}
